// src/pages/Reports/DailyScheduleQualityReportPage.js
//
// Daily Schedule Quality Report UI
// ============================================================================
//
// PURPOSE
// -------
// This page presents day-level schedule quality analytics based on completed
// and checked visit rows returned by the backend.
//
// MAIN BUSINESS QUESTIONS
// -----------------------
// - Which days were operationally compact and efficient?
// - Which days were fragmented by idle gaps?
// - Which days had good or poor continuity?
// - Which days generated strong revenue with strong density?
// - Which days look operationally weak vs. clean?
// - How much of a day was real business work vs. technical blocking vs. idle?
//
// WHY THIS REPORT IS IMPORTANT
// ----------------------------
// Other reports usually answer questions like:
// - which slots are strong,
// - which categories are strong,
// - which weekdays are strong,
// - which windows are idle.
//
// This page answers a different management question:
//   "How good was the actual structure of each day?"
//
// That makes it useful for:
// - operational review
// - booking quality improvement
// - identifying fragmented days
// - comparing day quality inside the same period
//
// IMPORTANT MODEL EVOLUTION
// -------------------------
// The backend now separates the day into three operational layers:
//
// 1) business work
//    - real client-service activity
//    - contributes to revenue and business utilization
//
// 2) technical blocked time
//    - technical calendar rows such as PRZERWA
//    - consumes time but is NOT business utilization
//
// 3) idle time
//    - time not covered by either business work or technical blocking
//
// This makes the report much more honest operationally:
// - a weak day may be weak because it was truly idle
// - or because technical blocks consumed too much time
// - or because business work never dominated the active span
//
// CURRENT DATA EXPECTATIONS
// -------------------------
// Backend payload shape:
//
// {
//   period,
//   filters,
//   methodology,
//   summary,
//   rows,
//   best_days,
//   worst_days
// }
//
// UI GOALS
// --------
// 1) Keep the page visually aligned with your other report pages.
// 2) Make the best and worst days easy to scan.
// 3) Keep the detailed ranking table readable and managerial.
// 4) Surface recommendation text as a helpful interpretation hint.
// 5) Show business vs. technical vs. idle layers clearly.
// 6) Add executive chart blocks that explain day quality in seconds.
//
// IMPORTANT ARCHITECTURAL NOTE
// ----------------------------
// This page:
// - uses shared reportService.js,
// - uses the shared evaluation period framework,
// - keeps transport/business calculations in backend,
// - does only local filtering / sorting / presentation work.
//
// I18N NOTE
// ---------
// This report intentionally keeps backend payload field names and stable
// backend classification codes unchanged. The frontend translates only
// user-facing labels, descriptions, chart legends, table headers, empty states
// and local UI messages. This protects cross-layer contracts while still
// allowing the UI to switch between English and Polish.
//

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../../context/AuthContext";
import { fetchUsers } from "../../services/userService";
import { fetchDailyScheduleQualityReport } from "../../services/reportService";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";

import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  ScatterChart,
  Scatter,
  Bar,
  Line,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine,
} from "recharts";

import "./DailyScheduleQualityReportPage.css";

/* -------------------------------------------------------------------------- */
/* Small formatting helpers                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Format money-like values with two decimals.
 */
function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

/**
 * Format minutes with one decimal.
 *
 * We intentionally keep one decimal place because averages and overlaps
 * may produce non-integer values and that precision is still useful.
 */
function formatMinutes(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.0";
  return n.toFixed(1);
}

/**
 * Format count-like values as rounded integers.
 */
function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return String(Math.round(n));
}

/**
 * General decimal formatter.
 *
 * This is used in chart tooltips and auxiliary displays where we want
 * generic 2-decimal formatting without implying a special business unit.
 */
function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

/**
 * Format ratio 0..1 as a percentage string.
 */
function formatPercentFromRatio(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.0%";
  return `${(n * 100).toFixed(1)}%`;
}

/**
 * Convert quality class into a stable badge class name.
 */
function qualityBadgeClassName(value) {
  const raw = String(value || "").toLowerCase();

  if (raw === "excellent") return "quality-badge quality-badge--excellent";
  if (raw === "good") return "quality-badge quality-badge--good";
  if (raw === "fair") return "quality-badge quality-badge--fair";
  if (raw === "weak") return "quality-badge quality-badge--weak";
  return "quality-badge quality-badge--poor";
}

/**
 * Render recommendation text safely.
 */
function formatRecommendation(value, t) {
  const raw = String(value || "").trim();

  if (raw) return raw;

  return typeof t === "function"
    ? t("reports:common.noRecommendationAvailable", {
        defaultValue: "No recommendation available.",
      })
    : "No recommendation available.";
}

/**
 * Translate backend schedule-quality class codes for display only.
 *
 * IMPORTANT
 * ---------
 * The raw values from the backend (excellent/good/fair/weak/poor) remain the
 * source for CSS class names, sorting, search compatibility and chart colors.
 * Only the visible label is localized here.
 */
function getQualityClassLabel(value, t) {
  const raw = String(value || "").toLowerCase();

  switch (raw) {
    case "excellent":
      return t("reports:dailyScheduleQuality.qualityClasses.excellent");
    case "good":
      return t("reports:dailyScheduleQuality.qualityClasses.good");
    case "fair":
      return t("reports:dailyScheduleQuality.qualityClasses.fair");
    case "weak":
      return t("reports:dailyScheduleQuality.qualityClasses.weak");
    case "poor":
      return t("reports:dailyScheduleQuality.qualityClasses.poor");
    default:
      return value || t("reports:common.unknown", { defaultValue: "Unknown" });
  }
}

/* -------------------------------------------------------------------------- */
/* Chart palette + helpers                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Stable local palette.
 *
 * Semantic intent:
 * - quality = blue family
 * - business revenue = green family
 * - business work = blue
 * - technical = amber
 * - idle = gray
 * - business ratio line = violet
 */
const CHART_COLORS = Object.freeze({
  quality: "#2563eb",
  qualityLight: "#60a5fa",

  revenue: "#16a34a",
  revenueLight: "#4ade80",

  business: "#2563eb",
  technical: "#f59e0b",
  idle: "#94a3b8",

  occupied: "#7c3aed",

  poor: "#dc2626",
  fair: "#f59e0b",
  good: "#2563eb",
  excellent: "#16a34a",

  grid: "#e5e7eb",
  axis: "#6b7280",
  tooltipBg: "#ffffff",
  tooltipBorder: "#d1d5db",
});

/**
 * Color mapping for the fragmentation scatter chart.
 */
function getQualityColor(qualityClass) {
  const value = String(qualityClass || "").toLowerCase();

  if (value === "excellent") return CHART_COLORS.excellent;
  if (value === "good") return CHART_COLORS.good;
  if (value === "fair") return CHART_COLORS.fair;
  if (value === "weak") return "#f97316";
  return CHART_COLORS.poor;
}

/**
 * Bubble-size helper for the scatter chart.
 *
 * We intentionally keep sizes inside a controlled visual band so one
 * extreme day does not ruin readability.
 */
function bubbleSizeFromRevenue(value, minRevenue, maxRevenue) {
  const v = Number(value) || 0;
  const min = Number(minRevenue) || 0;
  const max = Number(maxRevenue) || 0;

  if (max <= min) return 140;

  const ratio = (v - min) / (max - min);
  return 80 + ratio * 260;
}

/**
 * Chart-specific formatting helpers.
 */
function formatChartMoney(value) {
  return `${formatMoney(value)} PLN`;
}

function formatChartMinutes(value) {
  return `${formatMinutes(value)} min`;
}

function formatChartPercentFromRatio(value) {
  return formatPercentFromRatio(value);
}

/* -------------------------------------------------------------------------- */
/* Rich chart tooltips                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Timeline tooltip.
 *
 * This tooltip focuses on:
 * - quality score
 * - business revenue
 * - business / technical / idle structure
 * - business ratio
 */
function DailyQualityTimelineTooltip({ active, payload, label, t }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const row = payload?.[0]?.payload;

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">
        {label} {row?.weekday_label ? `• ${row.weekday_label}` : ""}
      </div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:dailyScheduleQuality.qualityScore")}</span>
          <strong>{formatCount(row?.schedule_quality_score)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.businessRevenue")}</span>
          <strong>{formatChartMoney(row?.total_business_revenue)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.businessMinutes")}</span>
          <strong>{formatChartMinutes(row?.total_business_booked_minutes)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.technicalBlocked")}</span>
          <strong>{formatChartMinutes(row?.total_technical_blocked_minutes)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.idleMinutes")}</span>
          <strong>{formatChartMinutes(row?.idle_minutes)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.businessRatio")}</span>
          <strong>{formatChartPercentFromRatio(row?.business_occupied_ratio)}</strong>
        </div>
      </div>

      {row?.recommendation ? (
        <div className="chart-tooltip__explanation">{row.recommendation}</div>
      ) : null}
    </div>
  );
}

/**
 * Scatter tooltip.
 *
 * This tooltip explains a single day as a point inside:
 * - fragmentation
 * - quality
 * - revenue
 */
function QualityFragmentationTooltip({ active, payload, t }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const row = payload?.[0]?.payload;
  if (!row) return null;

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">
        {row.date} • {row.weekday_label}
      </div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:dailyScheduleQuality.qualityScore")}</span>
          <strong>{formatCount(row.schedule_quality_score)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.totalIdleGap")}</span>
          <strong>{formatChartMinutes(row.total_idle_gap_minutes)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.businessRevenue")}</span>
          <strong>{formatChartMoney(row.total_business_revenue)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.gapCount")}</span>
          <strong>{formatCount(row.gap_count)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.businessRatio")}</span>
          <strong>{formatChartPercentFromRatio(row.business_occupied_ratio)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.businessVisits")}</span>
          <strong>{formatCount(row.total_business_visits)}</strong>
        </div>
      </div>

      <div className="chart-tooltip__explanation">
        {formatRecommendation(row.recommendation, t)}
      </div>
    </div>
  );
}

/**
 * Day structure tooltip.
 *
 * This is the executive tooltip for the stacked business / technical / idle chart.
 */
function DayStructureTooltip({ active, payload, label, t }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const row = payload?.[0]?.payload;
  if (!row) return null;

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">
        {label} {row?.weekday_label ? `• ${row.weekday_label}` : ""}
      </div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:dailyScheduleQuality.businessMinutes")}</span>
          <strong>{formatChartMinutes(row.businessMinutes)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.technicalMinutes")}</span>
          <strong>{formatChartMinutes(row.technicalMinutes)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.idleMinutes")}</span>
          <strong>{formatChartMinutes(row.idleMinutes)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.spanMinutes")}</span>
          <strong>{formatChartMinutes(row.spanMinutes)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.qualityScore")}</span>
          <strong>{formatCount(row.schedule_quality_score)}</strong>
        </div>
        <div>
          <span>{t("reports:dailyScheduleQuality.businessRevenue")}</span>
          <strong>{formatChartMoney(row.total_business_revenue)}</strong>
        </div>
      </div>

      {row?.recommendation ? (
        <div className="chart-tooltip__explanation">
          {formatRecommendation(row.recommendation, t)}
        </div>
      ) : null}
    </div>
  );
}

function resolveMethodologyNotes(methodology, t) {
  /**
   * Backend methodology text migration helper.
   *
   * The backend now sends stable methodology.note_keys so the frontend can own
   * the visible language through reports.json. Legacy methodology.notes remain
   * supported as a backward-compatible fallback while the report endpoints are
   * migrated incrementally.
   */
  const reportKind = methodology?.report_kind;

  if (reportKind && Array.isArray(methodology?.note_keys) && methodology.note_keys.length > 0) {
    return methodology.note_keys.map((noteKey) =>
      t(`reports:methodology.${reportKind}.notes.${noteKey}`, {
        defaultValue: noteKey,
      })
    );
  }

  return Array.isArray(methodology?.notes) ? methodology.notes : [];
}

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const DailyScheduleQualityReportPage = () => {
  const { t } = useTranslation(["common", "reports"]);
  const { authState } = useContext(AuthContext);

  const currentRole = authState?.user?.role || "";
  const currentUserId = authState?.user?.id;

  /**
   * Role-based allowed evaluation modes.
   *
   * Same principle as the other reporting pages:
   * - plain user -> narrower analysis freedom
   * - elevated roles -> broader analysis modes
   */
  const allowedEvaluationModes = useMemo(() => {
    if (currentRole === "user") {
      return [EVALUATION_PERIOD_TYPES.MONTH];
    }

    return [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ];
  }, [currentRole]);

  /**
   * Shared evaluation period controller.
   */
  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: allowedEvaluationModes,
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  /**
   * Local UI filters.
   *
   * userIds:
   *   sent to backend
   *
   * searchText:
   *   client-side search for the main table
   *
   * sortBy / sortDir:
   *   client-side sorting for the main table
   */
  const [filters, setFilters] = useState(() => ({
    userIds:
      currentRole === "user" && currentUserId != null ? [currentUserId] : [],
    searchText: "",
    sortBy: "schedule_quality_score",
    sortDir: "desc",
  }));

  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /**
   * Track the actually loaded period.
   *
   * This matches the UX pattern used across the reporting pages:
   * the picker may change, but the data only refreshes after Generate.
   */
  const [loadedPeriodMeta, setLoadedPeriodMeta] = useState({
    from: selectedFrom,
    to: selectedTo,
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  /* ------------------------------------------------------------------------ */
  /* Keep userIds aligned for role === "user"                                 */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (currentRole === "user" && currentUserId != null) {
      setFilters((prev) => ({
        ...prev,
        userIds: [currentUserId],
      }));
    }
  }, [currentRole, currentUserId]);

  /* ------------------------------------------------------------------------ */
  /* Load users for elevated-role employee filter                             */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsers();
        setUsers(Array.isArray(userList) ? userList : []);
      } catch (err) {
        console.error("[DailyScheduleQualityReportPage] ❌ Failed to fetch users:", err);
        setError(t("reports:dailyScheduleQuality.errors.failedToLoadUsers"));
      }
    };

    if (currentRole !== "user") {
      loadUsers();
    }
  }, [currentRole, t]);

  /* ------------------------------------------------------------------------ */
  /* Human-readable current-user display                                      */
  /* ------------------------------------------------------------------------ */

  const currentUserDisplay = useMemo(() => {
    const firstName = authState?.user?.first_name || "";
    const lastName = authState?.user?.last_name || "";
    const username = authState?.user?.username || "";

    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

    if (fullName && username) return `${fullName} (${username})`;
    if (fullName) return fullName;
    if (username) return username;

    return t("reports:common.currentUser", { defaultValue: "Current user" });
  }, [
    authState?.user?.first_name,
    authState?.user?.last_name,
    authState?.user?.username,
    t,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Fetch report                                                             */
  /* ------------------------------------------------------------------------ */

  const loadReportByRange = useCallback(
    async ({ from, to, userIds, displayLabel }) => {
      if (!from || !to) {
        setError(t("reports:common.invalidEvaluationPeriod"));
        return;
      }

      setLoading(true);
      setError("");

      try {
        const payload = await fetchDailyScheduleQualityReport(from, to, userIds);
        setReport(payload || null);

        setLoadedPeriodMeta({
          from,
          to,
          displayLabel: displayLabel || "",
        });
      } catch (err) {
        console.error("[DailyScheduleQualityReportPage] ❌ Error fetching report:", err);
        setError(t("reports:dailyScheduleQuality.errors.failedToFetch"));
        setReport(null);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  /**
   * Stable initial request.
   *
   * This prevents accidental reload drift caused by later UI edits before the
   * first report load happens.
   */
  const initialRequestRef = useRef({
    from: selectedFrom,
    to: selectedTo,
    userIds:
      currentRole === "user" && currentUserId != null ? [currentUserId] : [],
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  useEffect(() => {
    loadReportByRange(initialRequestRef.current);
  }, [loadReportByRange]);

  /* ------------------------------------------------------------------------ */
  /* Derived report sections                                                  */
  /* ------------------------------------------------------------------------ */

  const summary = report?.summary || {};
  const methodologyNotes = resolveMethodologyNotes(report?.methodology, t);

  const rows = Array.isArray(report?.rows) ? report.rows : [];
  const bestDays = Array.isArray(report?.best_days) ? report.best_days : [];
  const worstDays = Array.isArray(report?.worst_days) ? report.worst_days : [];

  const hasReportData = Boolean(report);

  /* ------------------------------------------------------------------------ */
  /* Chart datasets                                                           */
  /* ------------------------------------------------------------------------ */

  /**
   * Timeline chart dataset.
   *
   * Shows how business revenue and quality evolve through the selected period.
   */
  const timelineChartData = useMemo(() => {
    return [...rows]
      .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")))
      .map((row) => ({
        ...row,
        label: row.date,
      }));
  }, [rows]);

  /**
   * Scatter / fragmentation dataset.
   *
   * X = total idle gap minutes
   * Y = quality score
   * bubble size = business revenue
   */
  const fragmentationMatrixData = useMemo(() => {
    if (!rows.length) return [];

    const revenues = rows.map((row) => Number(row.total_business_revenue || 0));
    const minRevenue = Math.min(...revenues);
    const maxRevenue = Math.max(...revenues);

    return rows.map((row, index) => ({
      ...row,
      id: `${row.date || "day"}-${index}`,
      x: Number(row.total_idle_gap_minutes || 0),
      y: Number(row.schedule_quality_score || 0),
      z: bubbleSizeFromRevenue(
        row.total_business_revenue,
        minRevenue,
        maxRevenue
      ),
      color: getQualityColor(row.schedule_quality_class),
    }));
  }, [rows]);

  /**
   * Weekday overview dataset.
   *
   * This gives a compact weekday-level reading of:
   * - average quality score
   * - average business ratio
   * - optional supporting metrics available in tooltip
   */
  const weekdayOverviewData = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      const key = String(row.weekday_label || "Unknown");

      if (!map.has(key)) {
        map.set(key, {
          weekday_label: key,
          days_count: 0,
          total_quality_score: 0,
          total_idle_gap_minutes: 0,
          total_business_ratio: 0,
          total_business_revenue: 0,
        });
      }

      const agg = map.get(key);

      agg.days_count += 1;
      agg.total_quality_score += Number(row.schedule_quality_score || 0);
      agg.total_idle_gap_minutes += Number(row.total_idle_gap_minutes || 0);
      agg.total_business_ratio += Number(row.business_occupied_ratio || 0);
      agg.total_business_revenue += Number(row.total_business_revenue || 0);
    });

    const weekdayOrder = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    return Array.from(map.values())
      .map((row) => ({
        weekday_label: row.weekday_label,
        avg_quality_score:
          row.days_count > 0 ? row.total_quality_score / row.days_count : 0,
        avg_idle_gap_minutes:
          row.days_count > 0 ? row.total_idle_gap_minutes / row.days_count : 0,
        avg_business_ratio:
          row.days_count > 0 ? row.total_business_ratio / row.days_count : 0,
        avg_business_revenue:
          row.days_count > 0 ? row.total_business_revenue / row.days_count : 0,
        days_count: row.days_count,
      }))
      .sort(
        (a, b) =>
          weekdayOrder.indexOf(a.weekday_label) - weekdayOrder.indexOf(b.weekday_label)
      );
  }, [rows]);

  /**
   * Executive day-structure dataset.
   *
   * This is the strongest visual summary of the report:
   * - business minutes
   * - technical blocked minutes
   * - idle minutes
   *
   * It answers:
   *   "What was each day actually made of?"
   */
  const dayStructureChartData = useMemo(() => {
    return [...rows]
      .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")))
      .map((row) => ({
        date: row.date,
        weekday_label: row.weekday_label,

        businessMinutes: Number(row.total_business_booked_minutes || 0),
        technicalMinutes: Number(row.total_technical_blocked_minutes || 0),
        idleMinutes: Number(row.idle_minutes || 0),

        spanMinutes: Number(row.span_minutes || 0),
        schedule_quality_score: Number(row.schedule_quality_score || 0),
        total_business_revenue: Number(row.total_business_revenue || 0),
        recommendation: row.recommendation || "",
      }));
  }, [rows]);

  /* ------------------------------------------------------------------------ */
  /* Client-side filtering + sorting for the table                            */
  /* ------------------------------------------------------------------------ */

  /**
   * Main visible row set for the detailed table.
   *
   * IMPORTANT
   * ---------
   * The field names below are fully aligned with the current backend model.
   */
  const visibleRows = useMemo(() => {
    let out = [...rows];

    const searchText = String(filters.searchText || "").trim().toLowerCase();

    if (searchText) {
      out = out.filter((row) => {
        const date = String(row.date || "").toLowerCase();
        const weekday = String(row.weekday_label || "").toLowerCase();
        const recommendation = String(row.recommendation || "").toLowerCase();
        const qualityClass = String(row.schedule_quality_class || "").toLowerCase();
        const translatedQualityClass = getQualityClassLabel(
          row.schedule_quality_class,
          t
        ).toLowerCase();

        return (
          date.includes(searchText) ||
          weekday.includes(searchText) ||
          recommendation.includes(searchText) ||
          qualityClass.includes(searchText) ||
          translatedQualityClass.includes(searchText)
        );
      });
    }

    const sortBy = filters.sortBy || "schedule_quality_score";
    const sortDir = filters.sortDir === "asc" ? "asc" : "desc";

    out.sort((a, b) => {
      let cmp = 0;

      switch (sortBy) {
        case "date":
          cmp = String(a.date || "").localeCompare(String(b.date || ""));
          break;

        case "total_business_revenue":
          cmp =
            Number(a.total_business_revenue || 0) -
            Number(b.total_business_revenue || 0);
          break;

        case "total_business_booked_minutes":
          cmp =
            Number(a.total_business_booked_minutes || 0) -
            Number(b.total_business_booked_minutes || 0);
          break;

        case "business_occupied_ratio":
          cmp =
            Number(a.business_occupied_ratio || 0) -
            Number(b.business_occupied_ratio || 0);
          break;

        case "total_technical_blocked_minutes":
          cmp =
            Number(a.total_technical_blocked_minutes || 0) -
            Number(b.total_technical_blocked_minutes || 0);
          break;

        case "idle_minutes":
          cmp = Number(a.idle_minutes || 0) - Number(b.idle_minutes || 0);
          break;

        case "revenue_per_booked_business_hour":
          cmp =
            Number(a.revenue_per_booked_business_hour || 0) -
            Number(b.revenue_per_booked_business_hour || 0);
          break;

        case "schedule_quality_score":
        default:
          cmp =
            Number(a.schedule_quality_score || 0) -
            Number(b.schedule_quality_score || 0);
          break;
      }

      if (cmp === 0) {
        cmp = String(a.date || "").localeCompare(String(b.date || ""));
      }

      return sortDir === "asc" ? cmp : -cmp;
    });

    return out;
  }, [rows, filters.searchText, filters.sortBy, filters.sortDir, t]);

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="daily-schedule-quality-page">
      <div className="daily-schedule-quality-header">
        <div className="header-left">
          <h1>{t("reports:dailyScheduleQuality.title")}</h1>
          <p className="header-hint">{t("reports:dailyScheduleQuality.headerHint")}</p>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      {/* -------------------------------------------------------------------- */}
      {/* Filters bar                                                          */}
      {/* -------------------------------------------------------------------- */}
      <div className="filters-bar">
        <div className="filters-left">
          <EvaluationPeriodPicker period={evaluationPeriod} />

          {currentRole !== "user" ? (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employees")}</label>

              <select
                multiple
                className="user-dropdown"
                value={filters.userIds}
                onChange={(e) => {
                  const selectedValues = Array.from(
                    e.target.selectedOptions,
                    (o) => Number(o.value)
                  ).filter(Number.isFinite);

                  setFilters((prev) => ({
                    ...prev,
                    userIds: selectedValues,
                  }));
                }}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.username})
                  </option>
                ))}
              </select>

              <div className="filter-help">{t("reports:common.multiSelectHelp")}</div>
            </div>
          ) : (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employee")}</label>
              <input
                type="text"
                className="user-fixed"
                value={currentUserDisplay}
                readOnly
              />
            </div>
          )}

          <div className="filter-item filter-item-search">
            <label className="filter-label">{t("reports:common.search")}</label>
            <input
              type="text"
              className="search-input"
              value={filters.searchText}
              placeholder={t("reports:dailyScheduleQuality.searchPlaceholder")}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  searchText: e.target.value,
                }))
              }
            />
          </div>

          <div className="filter-item filter-item-sort">
            <label className="filter-label">{t("reports:dailyScheduleQuality.sortBy")}</label>
            <select
              className="sort-select"
              value={filters.sortBy}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  sortBy: e.target.value,
                }))
              }
            >
              <option value="schedule_quality_score">{t("reports:dailyScheduleQuality.qualityScore")}</option>
              <option value="total_business_revenue">{t("reports:dailyScheduleQuality.businessRevenue")}</option>
              <option value="total_business_booked_minutes">{t("reports:dailyScheduleQuality.businessMinutes")}</option>
              <option value="business_occupied_ratio">{t("reports:dailyScheduleQuality.businessOccupiedRatio")}</option>
              <option value="total_technical_blocked_minutes">{t("reports:dailyScheduleQuality.technicalBlockedMin")}</option>
              <option value="idle_minutes">{t("reports:dailyScheduleQuality.idleMinutes")}</option>
              <option value="revenue_per_booked_business_hour">{t("reports:dailyScheduleQuality.revenuePerBookedBusinessHour")}</option>
              <option value="date">{t("reports:common.date")}</option>
            </select>
          </div>

          <div className="filter-item filter-item-sortdir">
            <label className="filter-label">{t("reports:dailyScheduleQuality.direction")}</label>
            <select
              className="sort-select"
              value={filters.sortDir}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  sortDir: e.target.value,
                }))
              }
            >
              <option value="desc">{t("reports:dailyScheduleQuality.descending")}</option>
              <option value="asc">{t("reports:dailyScheduleQuality.ascending")}</option>
            </select>
          </div>
        </div>

        <div className="filters-actions">
          <button
            className="apply-button"
            onClick={() =>
              loadReportByRange({
                from: selectedFrom,
                to: selectedTo,
                userIds: filters.userIds,
                displayLabel: evaluationPeriod.displayLabel,
              })
            }
            disabled={loading}
          >
            {loading ? t("reports:common.loading") : t("reports:common.generate")}
          </button>
        </div>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* Main local scroll area                                               */}
      {/* -------------------------------------------------------------------- */}
      <div className="report-scroll">
        {loading ? (
          <div className="loading-panel">{t("reports:common.loading")}</div>
        ) : hasReportData ? (
          <>
            {/* -------------------------------------------------------------- */}
            {/* Summary cards                                                 */}
            {/* -------------------------------------------------------------- */}
            <div className="summary-grid">
              <div className="summary-card">
                <span className="summary-label">{t("reports:dailyScheduleQuality.activeDays")}</span>
                <span className="summary-value">
                  {formatCount(summary.active_days_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:dailyScheduleQuality.loadedRows")}</span>
                <span className="summary-value">
                  {formatCount(summary.completed_rows_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:dailyScheduleQuality.businessVisits")}</span>
                <span className="summary-value">
                  {formatCount(summary.total_business_visits)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:dailyScheduleQuality.technicalEntries")}</span>
                <span className="summary-value">
                  {formatCount(summary.total_technical_entries)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:dailyScheduleQuality.businessRevenue")}</span>
                <span className="summary-value">
                  {formatMoney(summary.total_business_revenue)} PLN
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:dailyScheduleQuality.businessMinutes")}</span>
                <span className="summary-value">
                  {formatCount(summary.total_business_booked_minutes)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:dailyScheduleQuality.technicalBlockedMin")}</span>
                <span className="summary-value">
                  {formatCount(summary.total_technical_blocked_minutes)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:dailyScheduleQuality.scheduledBlockedMin")}</span>
                <span className="summary-value">
                  {formatCount(summary.total_scheduled_blocked_minutes)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:dailyScheduleQuality.idleMinutes")}</span>
                <span className="summary-value">
                  {formatCount(summary.total_idle_minutes)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:dailyScheduleQuality.avgQualityScore")}</span>
                <span className="summary-value">
                  {formatCount(summary.average_schedule_quality_score)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:dailyScheduleQuality.excellentDays")}</span>
                <span className="summary-value">
                  {formatCount(summary.excellent_days_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:dailyScheduleQuality.poorDays")}</span>
                <span className="summary-value">
                  {formatCount(summary.poor_days_count)}
                </span>
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Highlight cards                                               */}
            {/* -------------------------------------------------------------- */}
            <div className="highlight-grid">
              <div className="highlight-card">
                <div className="highlight-title">{t("reports:dailyScheduleQuality.bestDay")}</div>
                {summary?.best_day ? (
                  <>
                    <div className="highlight-name">
                      {summary.best_day.date} • {summary.best_day.weekday_label}
                    </div>
                    <div className="highlight-metric">
                      {t("reports:dailyScheduleQuality.score")} {formatCount(summary.best_day.schedule_quality_score)}
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:dailyScheduleQuality.noData")}</div>
                )}
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:dailyScheduleQuality.worstDay")}</div>
                {summary?.worst_day ? (
                  <>
                    <div className="highlight-name">
                      {summary.worst_day.date} • {summary.worst_day.weekday_label}
                    </div>
                    <div className="highlight-metric">
                      {t("reports:dailyScheduleQuality.score")} {formatCount(summary.worst_day.schedule_quality_score)}
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:dailyScheduleQuality.noData")}</div>
                )}
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Methodology                                                   */}
            {/* -------------------------------------------------------------- */}
            <div className="methodology-card">
              <div className="methodology-title">{t("reports:dailyScheduleQuality.methodology")}</div>
              <ul className="methodology-list">
                {methodologyNotes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>

              {(loadedPeriodMeta?.from || loadedPeriodMeta?.to) && (
                <div className="loaded-period-note">
                  {t("reports:common.loadedPeriod")}: <strong>{loadedPeriodMeta.from}</strong> →{" "}
                  <strong>{loadedPeriodMeta.to}</strong>
                  {loadedPeriodMeta.displayLabel ? (
                    <> ({loadedPeriodMeta.displayLabel})</>
                  ) : null}
                </div>
              )}
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Charts                                                       */}
            {/* -------------------------------------------------------------- */}
            <div className="charts-grid">
              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <h2>{t("reports:dailyScheduleQuality.dailyStructureTitle")}</h2>
                  <p>{t("reports:dailyScheduleQuality.dailyStructureDescription")}</p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dayStructureChartData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 24 }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        dataKey="date"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 11 }}
                        angle={-20}
                        textAnchor="end"
                        interval="preserveStartEnd"
                        height={70}
                      />

                      <YAxis
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <Tooltip content={<DayStructureTooltip t={t} />} />
                      <Legend />

                      <Bar
                        dataKey="businessMinutes"
                        name={t("reports:dailyScheduleQuality.businessMinutes")}
                        stackId="dayStructure"
                        fill={CHART_COLORS.business}
                        radius={[6, 6, 0, 0]}
                      />

                      <Bar
                        dataKey="technicalMinutes"
                        name={t("reports:dailyScheduleQuality.technicalMinutes")}
                        stackId="dayStructure"
                        fill={CHART_COLORS.technical}
                      />

                      <Bar
                        dataKey="idleMinutes"
                        name={t("reports:dailyScheduleQuality.idleMinutes")}
                        stackId="dayStructure"
                        fill={CHART_COLORS.idle}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <h2>{t("reports:dailyScheduleQuality.dailyQualityTimelineTitle")}</h2>
                  <p>{t("reports:dailyScheduleQuality.dailyQualityTimelineDescription")}</p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={timelineChartData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 24 }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        dataKey="label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 11 }}
                        angle={-20}
                        textAnchor="end"
                        interval="preserveStartEnd"
                        height={70}
                      />

                      <YAxis
                        yAxisId="left"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 100]}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <Tooltip content={<DailyQualityTimelineTooltip t={t} />} />
                      <Legend />

                      <Bar
                        yAxisId="left"
                        dataKey="total_business_revenue"
                        name={t("reports:dailyScheduleQuality.businessRevenue")}
                        radius={[6, 6, 0, 0]}
                        fill={CHART_COLORS.revenue}
                      />

                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="schedule_quality_score"
                        name={t("reports:dailyScheduleQuality.qualityScore")}
                        stroke={CHART_COLORS.quality}
                        strokeWidth={2.5}
                        dot={{ r: 2, fill: CHART_COLORS.quality }}
                        activeDot={{ r: 5, fill: CHART_COLORS.quality }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:dailyScheduleQuality.qualityFragmentationTitle")}</h2>
                  <p>{t("reports:dailyScheduleQuality.qualityFragmentationDescription")}</p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 10, right: 24, bottom: 12, left: 8 }}>
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        type="number"
                        dataKey="x"
                        name={t("reports:dailyScheduleQuality.idleGapMinutes")}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        label={{
                          value: t("reports:dailyScheduleQuality.idleGapMinutes"),
                          position: "insideBottom",
                          offset: -4,
                          fill: CHART_COLORS.axis,
                        }}
                      />

                      <YAxis
                        type="number"
                        dataKey="y"
                        name={t("reports:dailyScheduleQuality.qualityScore")}
                        domain={[0, 100]}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        label={{
                          value: t("reports:dailyScheduleQuality.qualityScore"),
                          angle: -90,
                          position: "insideLeft",
                          fill: CHART_COLORS.axis,
                        }}
                      />

                      <ZAxis type="number" dataKey="z" range={[80, 320]} />
                      <Tooltip content={<QualityFragmentationTooltip t={t} />} />
                      <Legend />

                      <ReferenceLine
                        x={60}
                        stroke="#cbd5e1"
                        strokeDasharray="4 4"
                        ifOverflow="extendDomain"
                      />

                      <ReferenceLine
                        y={70}
                        stroke="#cbd5e1"
                        strokeDasharray="4 4"
                        ifOverflow="extendDomain"
                      />

                      <Scatter name={t("reports:dailyScheduleQuality.days")} data={fragmentationMatrixData}>
                        {fragmentationMatrixData.map((entry) => (
                          <Cell key={`quality-dot-${entry.id}`} fill={entry.color} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-legend-note">
                  <span className="legend-chip legend-chip--excellent">{t("reports:dailyScheduleQuality.qualityClasses.excellent")}</span>
                  <span className="legend-chip legend-chip--good">{t("reports:dailyScheduleQuality.qualityClasses.good")}</span>
                  <span className="legend-chip legend-chip--fair">{t("reports:dailyScheduleQuality.qualityClasses.fair")}</span>
                  <span className="legend-chip legend-chip--weak">{t("reports:dailyScheduleQuality.qualityClasses.weak")}</span>
                  <span className="legend-chip legend-chip--poor">{t("reports:dailyScheduleQuality.qualityClasses.poor")}</span>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:dailyScheduleQuality.weekdayQualityOverviewTitle")}</h2>
                  <p>{t("reports:dailyScheduleQuality.weekdayQualityOverviewDescription")}</p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={weekdayOverviewData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        dataKey="weekday_label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="left"
                        domain={[0, 100]}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 1]}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        tickFormatter={(value) => `${Math.round(value * 100)}%`}
                      />

                      <Tooltip
                        formatter={(value, name) => {
                          if (name === t("reports:dailyScheduleQuality.avgQualityScore")) {
                            return [formatNumber(value), name];
                          }

                          if (name === t("reports:dailyScheduleQuality.avgBusinessRatio")) {
                            return [formatPercentFromRatio(value), name];
                          }

                          if (name === t("reports:dailyScheduleQuality.avgIdleGap")) {
                            return [formatChartMinutes(value), name];
                          }

                          if (name === t("reports:dailyScheduleQuality.avgBusinessRevenue")) {
                            return [formatChartMoney(value), name];
                          }

                          return [value, name];
                        }}
                        contentStyle={{
                          backgroundColor: CHART_COLORS.tooltipBg,
                          border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                          borderRadius: 8,
                        }}
                      />

                      <Legend />

                      <Bar
                        yAxisId="left"
                        dataKey="avg_quality_score"
                        name={t("reports:dailyScheduleQuality.avgQualityScore")}
                        fill={CHART_COLORS.quality}
                        radius={[6, 6, 0, 0]}
                      />

                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="avg_business_ratio"
                        name={t("reports:dailyScheduleQuality.avgBusinessRatio")}
                        stroke={CHART_COLORS.occupied}
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: CHART_COLORS.occupied }}
                        activeDot={{ r: 5, fill: CHART_COLORS.occupied }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Best days                                                     */}
            {/* -------------------------------------------------------------- */}
            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:dailyScheduleQuality.topBestDaysTitle")}</h2>
                <p>{t("reports:dailyScheduleQuality.topBestDaysDescription")}</p>
              </div>

              <div className="day-card-grid">
                {bestDays.map((row) => (
                  <div className="day-card day-card--best" key={`best-${row.date}`}>
                    <div className="day-card__header">
                      <div className="day-card__title">
                        {row.date} • {row.weekday_label}
                      </div>
                      <span className={qualityBadgeClassName(row.schedule_quality_class)}>
                        {getQualityClassLabel(row.schedule_quality_class, t)}
                      </span>
                    </div>

                    <div className="day-card__metrics">
                      <div>
                        <span>{t("reports:dailyScheduleQuality.qualityScore")}</span>
                        <strong>{formatCount(row.schedule_quality_score)}</strong>
                      </div>
                      <div>
                        <span>{t("reports:dailyScheduleQuality.businessRevenue")}</span>
                        <strong>{formatMoney(row.total_business_revenue)} PLN</strong>
                      </div>
                      <div>
                        <span>{t("reports:dailyScheduleQuality.businessRatio")}</span>
                        <strong>{formatPercentFromRatio(row.business_occupied_ratio)}</strong>
                      </div>
                      <div>
                        <span>{t("reports:dailyScheduleQuality.technicalMin")}</span>
                        <strong>{formatMinutes(row.total_technical_blocked_minutes)} min</strong>
                      </div>
                      <div>
                        <span>{t("reports:dailyScheduleQuality.idleMin")}</span>
                        <strong>{formatMinutes(row.idle_minutes)} min</strong>
                      </div>
                      <div>
                        <span>{t("reports:dailyScheduleQuality.revenuePerBusinessHour")}</span>
                        <strong>{formatMoney(row.revenue_per_booked_business_hour)} PLN/h</strong>
                      </div>
                    </div>

                    <div className="day-card__text">{formatRecommendation(row.recommendation, t)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Worst days                                                    */}
            {/* -------------------------------------------------------------- */}
            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:dailyScheduleQuality.topWorstDaysTitle")}</h2>
                <p>{t("reports:dailyScheduleQuality.topWorstDaysDescription")}</p>
              </div>

              <div className="day-card-grid">
                {worstDays.map((row) => (
                  <div className="day-card day-card--worst" key={`worst-${row.date}`}>
                    <div className="day-card__header">
                      <div className="day-card__title">
                        {row.date} • {row.weekday_label}
                      </div>
                      <span className={qualityBadgeClassName(row.schedule_quality_class)}>
                        {getQualityClassLabel(row.schedule_quality_class, t)}
                      </span>
                    </div>

                    <div className="day-card__metrics">
                      <div>
                        <span>{t("reports:dailyScheduleQuality.qualityScore")}</span>
                        <strong>{formatCount(row.schedule_quality_score)}</strong>
                      </div>
                      <div>
                        <span>{t("reports:dailyScheduleQuality.businessRevenue")}</span>
                        <strong>{formatMoney(row.total_business_revenue)} PLN</strong>
                      </div>
                      <div>
                        <span>{t("reports:dailyScheduleQuality.businessRatio")}</span>
                        <strong>{formatPercentFromRatio(row.business_occupied_ratio)}</strong>
                      </div>
                      <div>
                        <span>{t("reports:dailyScheduleQuality.technicalMin")}</span>
                        <strong>{formatMinutes(row.total_technical_blocked_minutes)} min</strong>
                      </div>
                      <div>
                        <span>{t("reports:dailyScheduleQuality.idleMin")}</span>
                        <strong>{formatMinutes(row.idle_minutes)} min</strong>
                      </div>
                      <div>
                        <span>{t("reports:dailyScheduleQuality.revenuePerBusinessHour")}</span>
                        <strong>{formatMoney(row.revenue_per_booked_business_hour)} PLN/h</strong>
                      </div>
                    </div>

                    <div className="day-card__text">{formatRecommendation(row.recommendation, t)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Detailed ranking table                                        */}
            {/* -------------------------------------------------------------- */}
            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:dailyScheduleQuality.detailedRankingTitle")}</h2>
                <p>{t("reports:dailyScheduleQuality.detailedRankingDescription")}</p>
              </div>

              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("reports:common.date")}</th>
                      <th>{t("reports:dailyScheduleQuality.weekday")}</th>
                      <th>{t("reports:dailyScheduleQuality.quality")}</th>
                      <th>{t("reports:dailyScheduleQuality.score")}</th>
                      <th>{t("reports:dailyScheduleQuality.businessVisits")}</th>
                      <th>{t("reports:dailyScheduleQuality.techEntries")}</th>
                      <th>{t("reports:dailyScheduleQuality.businessMin")}</th>
                      <th>{t("reports:dailyScheduleQuality.techMin")}</th>
                      <th>{t("reports:dailyScheduleQuality.idleMin")}</th>
                      <th>{t("reports:dailyScheduleQuality.businessRevenue")}</th>
                      <th>{t("reports:dailyScheduleQuality.firstEvent")}</th>
                      <th>{t("reports:dailyScheduleQuality.lastEvent")}</th>
                      <th>{t("reports:dailyScheduleQuality.spanMin")}</th>
                      <th>{t("reports:dailyScheduleQuality.gapCount")}</th>
                      <th>{t("reports:dailyScheduleQuality.totalIdleGap")}</th>
                      <th>{t("reports:dailyScheduleQuality.businessRatio")}</th>
                      <th>{t("reports:dailyScheduleQuality.techPercentSpan")}</th>
                      <th>{t("reports:dailyScheduleQuality.totalBlockedPercentSpan")}</th>
                      <th>{t("reports:dailyScheduleQuality.revenuePerBusinessHour")}</th>
                      <th>{t("reports:dailyScheduleQuality.recommendation")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.length > 0 ? (
                      visibleRows.map((row) => (
                        <tr key={row.date}>
                          <td>{row.date}</td>
                          <td>{row.weekday_label}</td>
                          <td>
                            <span className={qualityBadgeClassName(row.schedule_quality_class)}>
                              {getQualityClassLabel(row.schedule_quality_class, t)}
                            </span>
                          </td>
                          <td>{formatCount(row.schedule_quality_score)}</td>
                          <td>{formatCount(row.total_business_visits)}</td>
                          <td>{formatCount(row.total_technical_entries)}</td>
                          <td>{formatCount(row.total_business_booked_minutes)}</td>
                          <td>{formatCount(row.total_technical_blocked_minutes)}</td>
                          <td>{formatCount(row.idle_minutes)}</td>
                          <td>{formatMoney(row.total_business_revenue)}</td>
                          <td>{row.first_event_start || "—"}</td>
                          <td>{row.last_event_end || "—"}</td>
                          <td>{formatCount(row.span_minutes)}</td>
                          <td>{formatCount(row.gap_count)}</td>
                          <td>{formatMinutes(row.total_idle_gap_minutes)}</td>
                          <td>{formatPercentFromRatio(row.business_occupied_ratio)}</td>
                          <td>{formatPercentFromRatio(row.technical_blocked_percent_of_span)}</td>
                          <td>{formatPercentFromRatio(row.total_blocked_percent_of_span)}</td>
                          <td>{formatMoney(row.revenue_per_booked_business_hour)} PLN/h</td>
                          <td className="wrap-cell">{formatRecommendation(row.recommendation, t)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={20} className="empty-table-cell">
                          {t("reports:dailyScheduleQuality.noDaysMatchFilters")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="empty-panel">{t("reports:common.noDataAvailable")}</div>
        )}
      </div>
    </div>
  );
};

export default DailyScheduleQualityReportPage;

